import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import {
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { CustomCatalogPage } from './components/catalog/IndexPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { caiseLightTheme } from './themes/caise-theme/caiseTheme';
import { UnifiedThemeProvider } from '@backstage/theme';
import LightThemeIcon from './components/assets/icons/LightThemeIcon';

import { gitlabAuthApiRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';

import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/pages/Home';
import { ExperimentsPage } from './components/pages/Experiments';
import { DevelopmentPage } from './components/pages/Development';
import { ServicesPage } from './components/pages/Services';
import { SignInPage } from './components/pages/SignIn';

import { coreComponentsTranslationRef } from '@backstage/core-components/alpha';
import { userSettingsTranslations } from './translations/userSettings';
import { catalogTranslations } from './translations/catalog';
import { catalogGraphTranslations } from './translations/catalogGraph';
import { catalogReactTranslations } from './translations/catalogReact';
import { searchTranslations, searchReactTranslations } from './translations/search';
import { coreComponentsTranslations } from './translations/core';
import { sidebarTranslations } from './translations/sidebar';
import { pagesTranslations } from './translations/pages';

import './styles.css';


const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const {t} = useTranslationRef(coreComponentsTranslationRef);
      return (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'gitlab-auth-provider',
            title: 'GitLab',
            message: (t('signIn.title') + ' (GitLab)'),
            apiRef: gitlabAuthApiRef,
          }}
        />
      )
    }
  },
  themes: [{
    id: 'caise-light-theme',
    title: 'light',
    variant: 'light',
    icon: <LightThemeIcon/>,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={caiseLightTheme} children={children} />
    ),
  }],
  __experimentalTranslations: {
    defaultLanguage: 'pl',
    availableLanguages: ['pl', 'en'],
    resources: [
        coreComponentsTranslations,
        userSettingsTranslations,
        catalogTranslations,
        catalogGraphTranslations,
        catalogReactTranslations,
        searchTranslations,
        searchReactTranslations,
        sidebarTranslations,
        pagesTranslations,
    ],
  }
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/experiments" element={<ExperimentsPage />} />
    <Route path="/development" element={<DevelopmentPage />} />
    <Route path="/services" element={<ServicesPage />} />
    <Route path="/catalog" element={<CatalogIndexPage />}>
        <CustomCatalogPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
